/** A chart of exactly the buckets the assistant's query_timeseries call returned (row B4). */
import { memo, useCallback, useMemo } from 'react';
import { EChartsMultiLineChart, type MultiLineSeriesConfig } from '../charts/echarts/EChartsMultiLineChart';
import type { AssistantChartBlock } from '../../store/assistantStore';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n: number) => String(n).padStart(2, '0');
/** Ruling R18: the model reads and writes UTC, so every time the chart shows is UTC too (never browser-local). */
const utcClock = (ms: number) => { const d = new Date(ms); return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`; };
const utcDay = (ms: number) => { const d = new Date(ms); return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`; };
const utcStamp = (ms: number) => `${utcDay(ms)} ${utcClock(ms)} UTC`;
const utcIso = (iso: string) => { const ms = Date.parse(iso); return Number.isFinite(ms) ? utcStamp(ms) : iso; };
const fmtVal = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** ECharts renders the tooltip formatter's string as HTML; unit and series text come from edge metadata. */
const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

const COLORS = { avg: '#38bdf8', min: '#94a3b8', max: '#94a3b8' } as const;

export const ChartBlockView = memo(function ChartBlockView({ chart }: { chart: AssistantChartBlock }) {
  const data = useMemo(() => {
    const byT = new Map<number, Record<string, number>>();
    for (const s of chart.series) for (const [t, v] of s.points) byT.set(t, { ...(byT.get(t) ?? { t }), [s.name]: v });
    return [...byT.values()].sort((a, b) => a.t - b.t);
  }, [chart]);
  const series = useMemo<MultiLineSeriesConfig[]>(() => chart.series.map((s) => ({
    dataKey: s.name, name: s.name, color: COLORS[s.name], unit: chart.unit ?? undefined,
    dashed: s.name !== 'avg', lineWidth: s.name === 'avg' ? 2 : 1,
  })), [chart]);
  // The chart primitive labels the y axis only through this formatter (series.unit is not read).
  const formatY = useCallback((v: number) => (chart.unit ? `${v} ${chart.unit}` : `${v}`), [chart.unit]);
  const isDaily = chart.interval === '1d';
  const formatX = useCallback((v: number | string) => {
    const ms = Number(v);
    return isDaily ? utcDay(ms) : utcClock(ms);
  }, [isDaily]);
  const formatTooltip = useCallback((params: unknown) => {
    const arr = (Array.isArray(params) ? params : [params]) as Array<{ axisValue?: number | string; seriesName?: string; value?: unknown }>;
    if (arr.length === 0) return '';
    const rows = arr.map((p) => {
      const y = Array.isArray(p.value) ? p.value[1] : p.value;
      const text = typeof y === 'number' && Number.isFinite(y) ? `${fmtVal(y)}${chart.unit ? ` ${escapeHtml(chart.unit)}` : ''}` : 'N/A';
      return `<div>${escapeHtml(p.seriesName ?? '')} ${text}</div>`;
    });
    return `<div>${utcStamp(Number(arr[0].axisValue))}</div>${rows.join('')}`;
  }, [chart.unit]);
  return (
    <figure className="mt-2 rounded-lg border border-ems-border bg-ems-bg p-2">
      <figcaption className="mb-1 text-[11px] font-medium text-ems-text-secondary">{chart.title}</figcaption>
      <EChartsMultiLineChart data={data} series={series} xAxisKey="t" leftYAxisFormatter={formatY} smooth={false} scaleYAxis xAxisFormatter={formatX} tooltipFormatter={formatTooltip} height={180} showLegend showTooltip />
      {chart.truncated && (
        <p className="mt-1 text-[10px] text-ems-amber">Capped — shows {utcIso(chart.coveredFrom)} … {utcIso(chart.coveredTo)}</p>
      )}
    </figure>
  );
});
